import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { userService, postService, followService, hashtagService, User, Post } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import FollowersModal from "../components/FollowersModal";
import {
  User as UserIcon,
  Calendar,
  Users,
  FileText,
  TrendingUp,
  UserPlus,
  UserMinus,
  Eye,
  Star,
  MessageCircle,
  Clock,
  PenTool,
  Search,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { enGB } from "date-fns/locale";
import toast from "react-hot-toast";

const BeatnikProfile: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { username } = useParams<{ username: string }>();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<User | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [checkingFollow, setCheckingFollow] = useState(false); // ✅ Nuevo estado
  const [followHover, setFollowHover] = useState(false);
  const [criteria, setCriteria] = useState<string>(searchParams.get("criteria") || "ranking");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const togglePost = (postId: string) => {
    setExpanded((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  // New state for followers modal
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followersModalType, setFollowersModalType] = useState<"followers" | "following">("followers");

  // States for hashtag search functionality
  const [timePeriod, setTimePeriod] = useState<string>(searchParams.get("time_period") || "");
  const [hashtags, setHashtags] = useState<{ hashtag: string; count: number }[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedHashtag, setSelectedHashtag] = useState<string>("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (username) {
      loadProfile();
      loadUserPosts();
      checkIfFollowing(); // ✅ Verificar si ya está siguiendo
    }
  }, [username, currentUser]); // ✅ Agregar currentUser como dependencia

  const loadProfile = async () => {
    try {
      if (username) {
        const profileData = await userService.getProfile(username);
        setProfile(profileData);
      }
    } catch (error) {
      toast.error("Error loading profile");
    } finally {
      setLoading(false);
    }
  };

  const loadUserPosts = async () => {
    try {
      if (username) {
        const userPosts = await postService.getPublicPostsByUsername(username, 0, 50);
        setPosts(userPosts);
      }
    } catch (error) {
      toast.error("Error loading articles");
    } finally {
      setPostsLoading(false);
    }
  };

  // ✅ Nueva función para verificar si ya está siguiendo
  const checkIfFollowing = async () => {
    if (!currentUser || !username || currentUser.username === username) {
      setIsFollowing(false);
      return;
    }

    try {
      setCheckingFollow(true);
      const resp = await userService.isFollowing(username);
      setIsFollowing(resp.is_following);
    } catch (error) {
      console.error("Error checking follow status:", error);
      setIsFollowing(false);
    } finally {
      setCheckingFollow(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser || !profile) {
      toast.error("You must sign in to follow users");
      return;
    }

    if (profile.username === currentUser.username) {
      toast.error("You cannot follow yourself");
      return;
    }

    setFollowLoading(true);
    try {
      const result = await followService.followUser(profile.username);
      setIsFollowing(!isFollowing);
      setFollowHover(false);

      // Update follower count
      setProfile({
        ...profile,
        followers_count: isFollowing ? profile.followers_count - 1 : profile.followers_count + 1,
      });

      toast.success(result.message);
    } catch (error) {
      toast.error("Error following/unfollowing user");
    } finally {
      setFollowLoading(false);
    }
  };

  const openFollowersModal = (type: "followers" | "following") => {
    setFollowersModalType(type);
    setShowFollowersModal(true);
  };

  const closeFollowersModal = () => {
    setShowFollowersModal(false);
  };

  // Hashtag search functions
  const loadHashtags = async () => {
    try {
      const hashtagData = await hashtagService.getPopularHashtags();
      setHashtags(hashtagData);
    } catch (error) {
      console.error("Error loading hashtags:", error);
    }
  };

  const updateURL = (newHashtag?: string, newTimePeriod?: string, newCriteria?: string) => {
    const params = new URLSearchParams();

    if (newHashtag) params.append("hashtag", newHashtag);
    if (newTimePeriod) params.append("time_period", newTimePeriod);
    if (newCriteria && newCriteria !== "ranking") params.append("criteria", newCriteria);

    setSearchParams(params);
  };

  const handleHashtagSearch = (hashtag: string) => {
    setSelectedHashtag(hashtag);
    setSearchInput(hashtag);
    setShowSuggestions(false);
    filterPostsByHashtag(hashtag);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    setShowSuggestions(value.length > 0);

    if (value === "") {
      setSelectedHashtag("");
      setPosts(allPosts.filter((post) => post.author_username === username));
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      handleHashtagSearch(searchInput.trim());
    }
  };

  const clearSearch = () => {
    setSearchInput("");
    setSelectedHashtag("");
    setShowSuggestions(false);
    setPosts(allPosts.filter((post) => post.author_username === username));
  };

  const handleTimePeriodChange = (period: string) => {
    setTimePeriod(period);
    updateURL(selectedHashtag, period, criteria);
  };

  const handleCriteriaChange = (newCriteria: string) => {
    setCriteria(newCriteria);
    updateURL(selectedHashtag, timePeriod, newCriteria);
  };

  const filterPostsByHashtag = (hashtag: string) => {
    const filtered = allPosts.filter(
      (post) =>
        post.author_username === username &&
        post.hashtags &&
        post.hashtags.some((tag) => tag.toLowerCase().includes(hashtag.toLowerCase()))
    );
    setPosts(filtered);
  };

  const filteredHashtagSuggestions = hashtags.filter((hashtag) =>
    hashtag.hashtag.toLowerCase().includes(searchInput.toLowerCase())
  );

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    return text.replace(regex, "<mark>$1</mark>");
  };

  // Load hashtags on component mount
  useEffect(() => {
    loadHashtags();
  }, []);

  // Store all posts when they are loaded
  useEffect(() => {
    if (posts.length > 0 && allPosts.length === 0) {
      setAllPosts(posts);
    }
  }, [posts]);

  // Handle outside click for suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const truncateContent = (content: string, maxLength: number = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <div className="beat-container">
        <div className="beat-card p-8 mb-8 rounded-lg">
          <div className="beat-loading h-32 mb-4"></div>
          <div className="beat-loading h-8 mb-2"></div>
          <div className="beat-loading h-4 mb-4"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="beat-container">
        <div className="beat-error">
          <h2 className="text-lg font-semibold mb-2">User not found</h2>
          <p>The profile you're looking for doesn't exist.</p>
          <Link to="/" className="beat-button mt-4 inline-block">
            Back to The Front Page
          </Link>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser?.username === profile.username;

  return (
    <div className="beat-container">
      {/* Profile Header */}
      <div className="w-full beatnik-profile-header px-4 sm:px-6 lg:px-14 flex flex-col md:flex-row pt-[16px] sm:pt-6 lg:pt-10 gap-[12px] sm:gap-4 lg:gap-[52px] justify-center items-start">
        {/* Avatar */}
        <div className="w-[96px] sm:w-[120px] md:w-[167px] h-[96px] sm:h-[120px] md:h-[167px] bg-[#D9D9D9] rounded-[8px] overflow-hidden shrink-0">
          {profile.avatar ? (
            <img
              src={profile.avatar}
              alt={profile.username}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div
              style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <UserIcon style={{ width: "60px", height: "60px", color: "#737373" }} />
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div
          className="Profile_Info"
          style={{
            width: "100%",
            background: "rgba(0, 0, 0, 0)",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: "7px",
            display: "inline-flex",
          }}
        >
          {/* Name and Rating */}
          <div
            className="Name_and_Rating"
            style={{
              alignSelf: "stretch",
              background: "rgba(0, 0, 0, 0)",
              justifyContent: "space-between",
              alignItems: "center",
              display: "inline-flex",
            }}
          >
            <div
              style={{
                width: "296px",
                justifyContent: "center",
                display: "flex",
                flexDirection: "column",
                color: "#262626",
                fontSize: "36px",
                fontFamily: "Abhaya Libre",
                fontWeight: "700",
                lineHeight: "40px",
                letterSpacing: "0.72px",
                wordWrap: "break-word",
              }}
            >
              {profile.username}
            </div>

            <div style={{ justifyContent: "flex-start", alignItems: "center", gap: "4px", display: "flex" }}>
              <Star className="w-5 h-5 text-newspaper-700 mx-auto" />

              <div
                style={{
                  color: "#404040",
                  fontSize: "14px",
                  fontFamily: "Inter",
                  fontWeight: "400",
                  wordWrap: "break-word",
                }}
              >
                {Math.round(profile.mojo * 10) / 10}
              </div>
            </div>
          </div>

          {/* Bio */}
          <div
            className="Biography_Profile"
            style={{
              width: "100%",
              maxWidth: "864px",
              color: "#525252",
              fontSize: "18px",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              lineHeight: "28px",
              wordWrap: "break-word",
            }}
          >
            {profile.bio ||
              "Modern wanderer documenting the poetry of movement and the philosophy of the open road. Finding truth in the spaces between destinations."}
          </div>

          {/* Stats Cards */}
          <div className="beatnik-profile-stats-cards self-stretch flex items-center justify-center md:justify-start gap-[5px] md:gap-5 flex-wrap md:flex-nowrap">
            {/* Mojo Card */}
            <div className="inline-flex w-full md:w-[204px] pl-[8px] md:pl-[10px] pr-[8px] md:pr-[10px] pt-[6px] md:pt-[19px] pb-[6px] md:pb-[19px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 flex-col justify-start items-center gap-[6px] md:gap-[7px]">
              <div className="relative overflow-hidden w-[28px] h-[28px] md:w-[34px] md:h-[34px]">
                <img src="/mojo_icon.png" alt="Mojo" style={{ width: "100%", height: "100%" }} />
              </div>
              <div className="flex flex-col justify-start items-center w-[120px] md:w-[139px] gap-[8px] md:gap-[10px]">
                <div
                  className="w-full text-center text-black text-[22px] md:text-[28px] leading-[18px] md:leading-[20px]"
                  style={{ fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
                >
                  {Math.round(profile.mojo)}
                </div>
                <div
                  className="w-full text-center flex flex-col justify-center text-black text-[12px] md:text-[14px]"
                  style={{ fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
                >
                  Mojo
                </div>
              </div>
            </div>

            {/* Articles Card */}
            <div className="inline-flex w-full md:w-[205px] pl-[8px] md:pl-[10px] pr-[8px] md:pr-[10px] pt-[6px] md:pt-[19px] pb-[6px] md:pb-[19px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 flex-col justify-start items-center gap-[6px] md:gap-[7px]">
              <div className="relative overflow-hidden w-[28px] h-[28px] md:w-[34px] md:h-[34px]">
                <FileText style={{ width: "32px", height: "32px", color: "black" }} />
              </div>
              <div className="flex flex-col justify-start items-center w-[120px] md:w-[139px] gap-[8px] md:gap-[10px]">
                <div
                  className="w-full text-center text-black text-[22px] md:text-[28px] leading-[18px] md:leading-[20px]"
                  style={{ fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
                >
                  {profile.posts_count}
                </div>
                <div
                  className="w-full text-center flex flex-col justify-center text-black text-[12px] md:text-[14px]"
                  style={{ fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
                >
                  Articles
                </div>
              </div>
            </div>

            {/* Followers Card */}
            <button
              onClick={() => openFollowersModal("followers")}
              className="inline-flex w-full md:w-[205px] pl-[8px] md:pl-[10px] pr-[8px] md:pr-[10px] pt-[6px] md:pt-[19px] pb-[6px] md:pb-[19px] min-h-[64px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 flex-col justify-start items-center gap-[8px] md:gap-[7px] bg-transparent border-none cursor-pointer"
            >
              <div className="relative overflow-hidden w-[28px] h-[28px] md:w-[34px] md:h-[34px]">
                <Users style={{ width: "32px", height: "32px", color: "black" }} />
              </div>
              <div className="flex flex-col justify-start items-center w-full md:w-[139px] gap-[8px] md:gap-[10px]">
                <div
                  className="w-full text-center text-black text-[22px] md:text-[28px] leading-[20px] md:leading-[20px]"
                  style={{ fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
                >
                  {profile.followers_count}
                </div>
                <div
                  className="w-full text-center flex flex-col justify-center text-black text-[12px] md:text-[14px]"
                  style={{ fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
                >
                  Followers
                </div>
              </div>
            </button>

            {/* Following Card */}
            <button
              onClick={() => openFollowersModal("following")}
              className="inline-flex w-full md:w-[205px] pl-[8px] md:pl-[10px] pr-[8px] md:pr-[10px] pt-[6px] md:pt-[19px] pb-[6px] md:pb-[19px] min-h-[64px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 flex-col justify-start items-center gap-[8px] md:gap-[7px] bg-transparent border-none cursor-pointer"
            >
              <div className="relative overflow-hidden w-[28px] h-[28px] md:w-[34px] md:h-[34px]">
                <Users style={{ width: "32px", height: "32px", color: "black" }} />
              </div>
              <div className="flex flex-col justify-start items-center w-full md:w-[139px] gap-[8px] md:gap-[10px]">
                <div
                  className="w-full text-center text-black text-[22px] md:text-[28px] leading-[20px] md:leading-[20px]"
                  style={{ fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
                >
                  {profile.following_count}
                </div>
                <div
                  className="w-full text-center flex flex-col justify-center text-black text-[12px] md:text-[14px]"
                  style={{ fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
                >
                  Following
                </div>
              </div>
            </button>
          </div>

          {/* Follow Button */}
          {!isOwnProfile && currentUser && (
            <div
              style={{
                borderRadius: "49px",
                outline: isFollowing ? "#97104a solid 1.5px" : "1px black solid",
                outlineOffset: "-1px",
                background: isFollowing ? "#97104a0d" : "white",
              }}
              className="Follow-button flex sm:inline-flex w-full sm:w-auto px-4 py-3 sm:py-[9px] justify-center items-center gap-[10px] cursor-pointer"
              onClick={handleFollow}
              onMouseEnter={() => setFollowHover(true)}
              onMouseLeave={() => setFollowHover(false)}
            >
              {followLoading || checkingFollow ? (
                <div className="beat-spinner"></div>
              ) : (
                <>
                  <div
                    style={{
                      color: isFollowing ? (followHover ? "#dc2626" : "#97104a") : "black",
                      fontSize: "14px",
                      fontFamily: "Abhaya Libre ExtraBold",
                      fontWeight: "800",
                      wordWrap: "break-word",
                    }}
                    className="text-[15px] sm:text-[14px]"
                  >
                    {checkingFollow ? "Loading..." : isFollowing ? (followHover ? "Unfollow" : "Following") : "Follow"}
                  </div>
                </>
              )}
            </div>
          )}

          {isOwnProfile && (
            <Link
              to="/profile"
              style={{
                paddingLeft: "16px",
                paddingRight: "16px",
                paddingTop: "9px",
                paddingBottom: "9px",
                borderRadius: "49px",
                outline: "1px black solid",
                outlineOffset: "-1px",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                display: "inline-flex",
                cursor: "pointer",
                background: "white",
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  color: "black",
                  fontSize: "14px",
                  fontFamily: "Abhaya Libre ExtraBold",
                  fontWeight: "800",
                  wordWrap: "break-word",
                }}
              >
                Edit profile
              </div>
            </Link>
          )}
        </div>
      </div>

      {/* Separator */}
      <div
        className="hidden lg:block"
        style={{
          width: "100%",
          height: "1px",
          backgroundColor: "rgb(211, 211, 211)",
          marginTop: "40px",
          marginBottom: "40px",
        }}
      />

      {/* Header with Search and Filters */}
      <div
        className="beat-header-search-filters mt-3 md:mt-0"
        style={{
          width: "100%",
          // paddingTop: "40px",
          paddingBottom: "20px",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: "19px",
          display: "inline-flex",
        }}
      >
        <div
          style={{
            alignSelf: "stretch",
            padding: "15px",
            background: "#FFFFFF",
            outline: "1px #E5E5E5 solid",
            outlineOffset: "-1px",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "10px",
            display: "flex",
          }}
        >
          <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
            <div className="relative" ref={searchRef}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-newspaper-400 w-5 h-5" />
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchInputChange}
                onFocus={() => setShowSuggestions(searchInput.length > 0)}
                placeholder="Search by hashtag (partial matching supported)..."
                className="w-full pl-10 pr-12 py-3 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-newspaper-400 hover:text-newspaper-600 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {showSuggestions && filteredHashtagSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-newspaper-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredHashtagSuggestions.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleHashtagSearch(item.hashtag)}
                    className="px-4 py-3 hover:bg-newspaper-50 cursor-pointer border-b border-newspaper-100 last:border-b-0"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-newspaper-700 font-medium">
                        #{highlightMatch(item.hashtag, searchInput)}
                      </span>
                      <span className="text-newspaper-400 text-sm">{item.count} posts</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </form>
        </div>

        <div className="inline-flex w-full items-center justify-center md:justify-start gap-2 md:gap-[10px] flex-wrap md:flex-nowrap">
          {hashtags.slice(0, 6).map((item, index) => (
            <div
              key={item.hashtag}
              data-property-1={index === 0 ? "Default" : "Variant2"}
              className="flex items-center justify-center gap-[10px] cursor-pointer rounded-[49px] pl-[12px] md:pl-[16px] pr-[12px] md:pr-[16px] pt-[8px] md:pt-[9px] pb-[8px] md:pb-[9px]"
              style={{
                background: selectedHashtag === item.hashtag ? "#262626" : "transparent",
                outline: selectedHashtag === item.hashtag ? "none" : "1px black solid",
                outlineOffset: "-1px",
              }}
              onClick={() => handleHashtagSearch(item.hashtag)}
            >
              <div
                className="text-[13px] md:text-[14px]"
                style={{
                  color: selectedHashtag === item.hashtag ? "white" : "black",
                  fontFamily: "Abhaya Libre ExtraBold",
                  fontWeight: "800",
                  wordWrap: "break-word",
                }}
              >
                #{item.hashtag}
              </div>
            </div>
          ))}
        </div>

        {/* Sort by time period */}
        <div
          className="sort-by-time-period flex flex-col md:flex-row w-full gap-[10px] md:gap-4"
          style={{ background: "rgba(0, 0, 0, 0)" }}
        >
          <div
            className="h-[20px] text-[14px] md:text-[16px]"
            style={{
              width: "auto",
              color: "#404040",
              fontFamily: "Abhaya Libre",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            Sort by:
          </div>
          <div className="relative w-full md:w-auto">
            <select
              value={criteria}
              onChange={(e) => handleCriteriaChange(e.target.value)}
              className="w-full md:w-auto pl-[12px] md:pl-[15px] pr-[36px] md:pr-[40px] py-[8px] bg-white rounded-[39px] md:rounded-[68px] text-black text-[14px]"
              style={{
                outline: "1px #D4D4D4 solid",
                outlineOffset: "-1px",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                border: "none",
                appearance: "none",
                cursor: "pointer",
              }}
            >
              <option value="ranking">Higher ranking</option>
              <option value="visits">Most visited</option>
            </select>
            <div
              style={{
                position: "absolute",
                right: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                width: "21px",
                height: "21px",
              }}
            >
              <div
                style={{
                  width: "10.52px",
                  height: "6.32px",
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  background: "black",
                  clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                }}
              />
            </div>
          </div>
          <div className="relative h-[20px]" style={{ width: "84px", background: "rgba(0, 0, 0, 0)" }}>
            <div
              className="absolute left-0 top-[1px] text-[14px] md:text-[16px]"
              style={{
                width: "auto",
                height: "20px",
                color: "#404040",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                wordWrap: "break-word",
              }}
            >
              Time period:
            </div>
          </div>
          <div className="relative w-full md:w-auto">
            <select
              value={timePeriod}
              onChange={(e) => handleTimePeriodChange(e.target.value)}
              className="w-full md:w-auto pl-[12px] md:pl-[15px] pr-[36px] md:pr-[40px] py-[8px] bg-white rounded-[39px] text-black text-[14px]"
              style={{
                outline: "1px #D4D4D4 solid",
                outlineOffset: "-1px",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                border: "none",
                appearance: "none",
                cursor: "pointer",
              }}
            >
              <option value="">All time</option>
              <option value="24h">Past 24h</option>
              <option value="7d">Past 7 days</option>
              <option value="30d">Past 30 days</option>
            </select>
            <div
              style={{
                position: "absolute",
                right: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                width: "21px",
                height: "21px",
              }}
            >
              <div
                style={{
                  width: "10.52px",
                  height: "6.32px",
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  background: "black",
                  clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* User's Posts */}
      <div
        className="articles-list space-y-3 sm:space-y-4 md:space-y-6 px-4 sm:px-6 md:px-11"
        style={{
          paddingBottom: "60px",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          display: "flex",
        }}
      >
        {postsLoading ? (
          <div className="space-y-6 w-full">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="beat-card p-6">
                <div className="beat-loading h-6 mb-4"></div>
                <div className="beat-loading h-4 mb-2"></div>
                <div className="beat-loading h-32"></div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="beat-card p-8 rounded-lg text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-newspaper-400" />
            <h3 className="text-xl font-semibold text-newspaper-700 mb-2">No articles yet</h3>
            <p className="text-newspaper-600">
              {isOwnProfile
                ? "You haven't published any articles yet. Start writing!"
                : `${profile.username} hasn't published any articles yet.`}
            </p>
            {isOwnProfile && (
              <Link to="/write" className="beat-button mt-4 flex items-center space-x-2">
                <PenTool className="w-4 h-4" />
                <span>Write article</span>
              </Link>
            )}
          </div>
        ) : (
          posts.map((post, index) => {
            console.log("BeatnikProfile: post content", { id: post.id, title: post.title, content: post.content });
            return (
              <div
                key={post.id}
                className="beat-card-rannkings flex-wrap sm:flex-nowrap"
                data-property-1="Variant3"
                style={{
                  width: "100%",
                  background: "rgba(0, 0, 0, 0)",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: typeof window !== "undefined" && window.innerWidth < 640 && !expanded[post.id] ? "12px" : "28px",
                  display: "inline-flex",
                  overflow: "hidden",
                  marginBottom:
                    typeof window !== "undefined" && window.innerWidth < 640 && !expanded[post.id] ? "8px" : "24px",
                }}
                onClick={() => {
                  if (typeof window !== "undefined" && window.innerWidth < 640) {
                    if (!expanded[post.id]) {
                      setExpanded((prev) => ({ ...prev, [post.id]: true }));
                      return;
                    }
                    navigate(`/post/${post.id}`);
                  }
                }}
              >
                {expanded[post.id] && (
                  <img
                    style={{
                      width: "143px",
                      height: "143px",
                      background: "#D9D9D9",
                      objectFit: "cover",
                    }}
                    className="beat-image-bw"
                    src={post.image || "/Beatpost_defaut_image.png"}
                    alt=""
                  />
                )}
                <div
                  style={{
                    width: "100%",
                    background: "rgba(0, 0, 0, 0)",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: "9px",
                    display: "inline-flex",
                  }}
                >
                  <div
                    style={{
                      alignSelf: "stretch",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <Link
                      to={`/post/${post.id}`}
                      style={{ textDecoration: "none", flex: 1 }}
                      onClick={(e) => {
                        if (typeof window !== "undefined" && window.innerWidth < 640) {
                          if (!expanded[post.id]) {
                            e.preventDefault();
                            setExpanded((prev) => ({ ...prev, [post.id]: true }));
                          }
                        }
                      }}
                    >
                      <div
                        style={{
                          color: "#262626",
                          fontSize: "20px",
                          fontFamily: "Abhaya Libre",
                          fontWeight: "700",
                          lineHeight: "25px",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.title}
                      </div>
                    </Link>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePost(post.id);
                      }}
                      className="xl:hidden"
                      style={{
                        background: "transparent",
                        border: "none",
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        cursor: "pointer",
                      }}
                      aria-expanded={!!expanded[post.id]}
                    >
                      {expanded[post.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>
                  {expanded[post.id] && (
                    <div
                      style={{
                        alignSelf: "stretch",
                        color: "#525252",
                        fontSize: "14px",
                        fontFamily: "Instrument Sans",
                        fontWeight: "400",
                        lineHeight: "20px",
                        wordWrap: "break-word",
                      }}
                    >
                      {truncateContent(post.content)}
                    </div>
                  )}
                  {expanded[post.id] && (
                    <div
                      className="gap-2 flex flex-row items-center"
                      style={{
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Link
                        to={`/voice/${post.author_username || profile.username}`}
                        className="Container flex justify-between items-center flex-row gap-2 hover:text-newspaper-900"
                      >
                        <img
                          src={post.author_avatar || profile.avatar || "/default-avatar.png"}
                          alt={post.author_username || profile.username}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                        <div
                          style={{
                            width: "130px",
                            height: "16px",
                            fontFamily: "Abhaya Libre",
                            fontWeight: "400",
                            lineHeight: "16px",
                            letterSpacing: "0.6px",
                            wordWrap: "break-word",
                            color: "#737373",
                            fontSize: "14px",
                          }}
                        >
                          By {post.author_username || profile.username}
                        </div>
                      </Link>

                      {post.hashtags.slice(0, 3).map((tag, tagIndex) => (
                        <div
                          key={tag}
                          onClick={() => handleHashtagSearch(tag)}
                          style={{
                            width: tagIndex === 0 ? "min-content" : "max-content",
                            height: "16px",
                            color: "#A3A3A3",
                            fontSize: "14px",
                            fontFamily: "Abhaya Libre",
                            fontWeight: "400",
                            lineHeight: "16px",
                            wordWrap: "break-word",
                            cursor: "pointer",
                          }}
                        >
                          #{tag}
                        </div>
                      ))}
                    </div>
                  )}
                  {expanded[post.id] && (
                    <div
                      style={{
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "space-between",
                        alignItems: "center",
                        display: "inline-flex",
                      }}
                    >
                      <div
                        style={{
                          background: "rgba(0, 0, 0, 0)",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: "8px",
                          display: "flex",
                        }}
                      >
                        <Star className="w-4 h-4" style={{ color: "#737373" }} />

                        <div
                          style={{
                            textAlign: "center",
                            color: "#737373",
                            fontSize: "13px",
                            fontFamily: "Abhaya Libre ExtraBold",
                            fontWeight: "800",
                            lineHeight: "16px",
                            letterSpacing: "0.6px",
                            wordWrap: "break-word",
                          }}
                        >
                          {post.average_rating.toFixed(1)}
                        </div>
                      </div>
                      <div
                        style={{
                          alignSelf: "stretch",
                          background: "rgba(0, 0, 0, 0)",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "8px",
                          display: "flex",
                        }}
                      >
                        <Eye className="w-4 h-4" style={{ color: "#737373" }} />

                        <div
                          style={{
                            color: "#737373",
                            fontSize: "13px",
                            fontFamily: "Abhaya Libre ExtraBold",
                            fontWeight: "800",
                            wordWrap: "break-word",
                          }}
                        >
                          {post.visits.toLocaleString()} visits
                        </div>
                      </div>
                      <div
                        style={{
                          width: "98.12px",
                          alignSelf: "stretch",
                          background: "rgba(0, 0, 0, 0)",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: "8px",
                          display: "flex",
                        }}
                      >
                        <MessageCircle className="w-4 h-4" style={{ color: "#737373" }} />

                        <div
                          style={{
                            color: "#737373",
                            fontSize: "13px",
                            fontFamily: "Abhaya Libre ExtraBold",
                            fontWeight: "800",
                            wordWrap: "break-word",
                          }}
                        >
                          {post.comments_count} comments
                        </div>
                      </div>

                      <div
                        className="hidden sm:block"
                        style={{
                          width: "28.61px",
                          height: "16px",
                          position: "relative",
                          background: "rgba(0, 0, 0, 0)",
                        }}
                      >
                        <div
                          style={{
                            width: "31px",
                            height: "16px",
                            left: "0px",
                            top: "0px",
                            position: "absolute",
                            color: "#525252",
                            fontSize: "13px",
                            fontFamily: "Abhaya Libre ExtraBold",
                            fontWeight: "800",
                            wordWrap: "break-word",
                          }}
                        >
                          ↑ +{index + 1}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Followers Modal */}
      <FollowersModal
        isOpen={showFollowersModal}
        onClose={closeFollowersModal}
        username={profile?.username || ""}
        type={followersModalType}
      />
    </div>
  );
};

export default BeatnikProfile;
